import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Slider,
  Tooltip,
} from '@mui/material';
import {
  buildThemeColorsFromPrimary,
  clamp,
  contrastText,
  hexToHsl,
  hexToRgb,
  hslToHex,
  isValidHex,
  QUICK_SWATCHES,
  rgbToHex,
} from '../utils/colorMath';

type Props = {
  value: string;
  onChange: (hex: string) => void;
  onApply?: (palette: ReturnType<typeof buildThemeColorsFromPrimary>) => void;
  showApply?: boolean;
  compact?: boolean;
};

const AdvancedColorPicker: React.FC<Props> = ({
  value,
  onChange,
  onApply,
  showApply = false,
  compact = false,
}) => {
  const planeRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [hue, setHue] = useState(() => hexToHsl(value).h);
  const [sat, setSat] = useState(() => hexToHsl(value).s);
  const [light, setLight] = useState(() => hexToHsl(value).l);
  const [hexInput, setHexInput] = useState(value.toUpperCase());
  const rgb = hexToRgb(value);

  useEffect(() => {
    if (!isValidHex(value)) return;
    const hsl = hexToHsl(value);
    setHue(hsl.h);
    setSat(hsl.s);
    setLight(hsl.l);
    setHexInput(value.toUpperCase());
  }, [value]);

  const emit = useCallback(
    (h: number, s: number, l: number) => {
      const hex = hslToHex(h, s, l);
      setHexInput(hex.toUpperCase());
      onChange(hex);
    },
    [onChange]
  );

  const pickFromPointer = (clientX: number, clientY: number) => {
    const el = planeRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clamp((clientX - rect.left) / rect.width, 0, 1);
    const y = clamp((clientY - rect.top) / rect.height, 0, 1);
    const nextSat = Math.round(x * 100);
    const nextLight = Math.round((1 - y) * 100);
    setSat(nextSat);
    setLight(nextLight);
    emit(hue, nextSat, nextLight);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    pickFromPointer(e.clientX, e.clientY);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    pickFromPointer(e.clientX, e.clientY);
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  const setFromHex = (raw: string) => {
    const next = raw.startsWith('#') ? raw : `#${raw}`;
    setHexInput(next.toUpperCase());
    if (isValidHex(next)) {
      const hsl = hexToHsl(next);
      setHue(hsl.h);
      setSat(hsl.s);
      setLight(hsl.l);
      onChange(next);
    }
  };

  const setRgbChannel = (channel: 'r' | 'g' | 'b', nextVal: number) => {
    const next = {
      r: channel === 'r' ? nextVal : rgb.r,
      g: channel === 'g' ? nextVal : rgb.g,
      b: channel === 'b' ? nextVal : rgb.b,
    };
    const hex = rgbToHex(next.r, next.g, next.b);
    const hsl = hexToHsl(hex);
    setHue(hsl.h);
    setSat(hsl.s);
    setLight(hsl.l);
    setHexInput(hex.toUpperCase());
    onChange(hex);
  };

  const planeH = compact ? 140 : 180;

  return (
    <Box>
      <Box
        ref={planeRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: 'relative',
          height: planeH,
          borderRadius: 2,
          cursor: 'crosshair',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          background: `
            linear-gradient(to top, #000, transparent),
            linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))
          `,
          touchAction: 'none',
          userSelect: 'none',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${sat}%`,
            top: `${100 - light}%`,
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.35)',
            transform: 'translate(-50%, -50%)',
            bgcolor: value,
            pointerEvents: 'none',
          }}
        />
      </Box>

      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
        Hue
      </Typography>
      <Slider
        value={hue}
        min={0}
        max={360}
        onChange={(_, v) => {
          const h = v as number;
          setHue(h);
          emit(h, sat, light);
        }}
        sx={{
          mb: 1.5,
          height: 10,
          borderRadius: 999,
          padding: '10px 0',
          '& .MuiSlider-rail': {
            opacity: 1,
            background:
              'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
          },
          '& .MuiSlider-track': { background: 'transparent', border: 'none' },
          '& .MuiSlider-thumb': {
            width: 18,
            height: 18,
            bgcolor: hslToHex(hue, 100, 50),
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
          },
        }}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        {QUICK_SWATCHES.map((swatch) => (
          <Tooltip key={swatch} title={swatch}>
            <Box
              component="button"
              type="button"
              onClick={() => setFromHex(swatch)}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: value.toLowerCase() === swatch.toLowerCase() ? '2px solid' : '1px solid',
                borderColor: value.toLowerCase() === swatch.toLowerCase() ? 'text.primary' : 'divider',
                bgcolor: swatch,
                cursor: 'pointer',
                p: 0,
                outline: 'none',
                boxShadow:
                  value.toLowerCase() === swatch.toLowerCase()
                    ? `0 0 0 2px ${swatch}55`
                    : 'none',
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            bgcolor: value,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            color: contrastText(value),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.65rem',
            fontWeight: 700,
          }}
        >
          Aa
        </Box>
        <TextField
          size="small"
          label="Hex"
          value={hexInput}
          onChange={(e) => setFromHex(e.target.value)}
          sx={{ flex: 1, '& input': { fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem' } }}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: showApply ? 1.5 : 0 }}>
        {(['r', 'g', 'b'] as const).map((ch) => (
          <TextField
            key={ch}
            size="small"
            label={ch.toUpperCase()}
            type="number"
            value={rgb[ch]}
            inputProps={{ min: 0, max: 255 }}
            onChange={(e) => setRgbChannel(ch, clamp(Number(e.target.value) || 0, 0, 255))}
            sx={{ flex: 1, '& input': { fontSize: '0.8rem' } }}
          />
        ))}
      </Stack>

      {showApply && onApply && (
        <Button
          fullWidth
          variant="contained"
          onClick={() => onApply(buildThemeColorsFromPrimary(value))}
          sx={{
            mt: 0.5,
            bgcolor: value,
            color: contrastText(value),
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { bgcolor: value, filter: 'brightness(0.92)' },
          }}
        >
          Apply custom color
        </Button>
      )}
    </Box>
  );
};

export default AdvancedColorPicker;
